import React from 'react';
import { useAuth } from '../../context/AuthContext';
import ConfirmModal from '../common/ConfirmModal';
import RunningJobCard from './pipeline/RunningJobCard';
import StatusPane from './pipeline/StatusPane';
import useDistrictJobs from './pipeline/useDistrictJobs';

/**
 * Pipeline jobs: running jobs on the left with whole-job time left and one row per
 * IEP cycle; the right status pane holds the queue, paused and finished jobs.
 */
export default function PipelineJobsPage() {
  const { user } = useAuth();
  const isDirector = user?.role === 1;
  const jobs = useDistrictJobs({ isDirector });
  const { data, loading, stale, alert, canControl, confirm } = jobs;

  return (
    <div className="mx-auto flex w-full min-w-0 flex-col gap-4 sm:gap-5 lg:h-[calc(100dvh-4rem-2.5rem)] lg:max-h-[calc(100dvh-4rem-2.5rem)] lg:overflow-hidden">
      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmText={confirm?.confirmText}
        variant={confirm?.variant || 'info'}
        loading={confirm?.kind === 'intake' ? jobs.intakeBusy : !!jobs.busyId}
        onConfirm={jobs.runConfirmed}
        onCancel={jobs.cancelConfirm}
      />

      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <div className="min-w-[240px] flex-1">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">Pipeline jobs</h1>
        </div>
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-semibold ${
          data.intake_paused
            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
            : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'}`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${data.intake_paused ? 'bg-amber-500' : 'bg-emerald-500'}`} />
          {data.intake_paused ? 'Intake paused' : 'Intake open'}
        </span>
        {canControl ? (
          <button
            type="button"
            disabled={jobs.intakeBusy}
            onClick={() => jobs.requestIntake(!data.intake_paused)}
            className={`min-h-[38px] rounded-xl px-4 text-[13px] font-semibold disabled:opacity-50 ${
              data.intake_paused
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'border border-amber-400 text-amber-800 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-900/20'}`}
          >
            {data.intake_paused ? 'Resume intake' : 'Pause intake'}
          </button>
        ) : null}
        <button
          type="button"
          onClick={jobs.refresh}
          className="min-h-[38px] rounded-xl border border-gray-300 px-4 text-[13px] font-semibold text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
        >
          Refresh
        </button>
      </div>

      {alert ? (
        <div
          role="status"
          className={`flex items-start justify-between gap-2 rounded-xl border px-3.5 py-2.5 text-[13px] ${
            alert.type === 'error'
              ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300'
              : alert.type === 'warning'
                ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300'
                : 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-800 dark:bg-sky-900/20 dark:text-sky-300'}`}
        >
          <p className="min-w-0">{alert.message}</p>
          <button type="button" aria-label="Dismiss" onClick={jobs.dismissAlert} className="opacity-70 hover:opacity-100">✕</button>
        </div>
      ) : null}

      <div className={`grid min-h-0 flex-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)] lg:items-stretch ${stale ? 'opacity-80' : ''}`}>
        <div className="flex min-h-0 min-w-0 flex-col gap-4 lg:overflow-y-auto">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <h2 className="text-[15px] font-semibold text-gray-900 dark:text-white">Running now</h2>
            <span className="text-[12px] text-gray-500 dark:text-slate-400">
              {data.running.length} job{data.running.length === 1 ? '' : 's'}
              {' · '}
              {Number(data.slots?.max_parallel) || 1} worker{(Number(data.slots?.max_parallel) || 1) === 1 ? '' : 's'}
            </span>
          </div>
          {loading && !data.running.length ? (
            <div className="rounded-2xl border border-gray-200 bg-white px-5 py-8 text-center text-sm text-gray-500 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-400">
              Loading jobs…
            </div>
          ) : data.running.length ? (
            data.running.map((job) => (
              <RunningJobCard
                key={job.job_id}
                job={job}
                now={jobs.now}
                busy={jobs.busyId === job.job_id}
                onAction={jobs.requestJobAction}
              />
            ))
          ) : (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-transparent px-5 py-8 text-center dark:border-slate-600">
              <p className="text-[14px] font-semibold text-gray-800 dark:text-slate-100">
                {stale ? 'Jobs could not be refreshed' : 'No jobs running'}
              </p>
              <p className="mx-auto mt-1 max-w-md text-[12.5px] text-gray-500 dark:text-slate-400">
                {stale
                  ? 'The district pipeline is unreachable right now. Use Refresh to try again — queued and finished lists stay empty until a load succeeds.'
                  : data.queued.length
                    ? 'Queued jobs start as soon as a worker is free.'
                    : 'Uploads and compliance re-runs appear here while they process.'}
              </p>
              {stale ? (
                <button
                  type="button"
                  onClick={jobs.refresh}
                  className="mt-4 min-h-[36px] rounded-xl bg-indigo-600 px-4 text-[13px] font-semibold text-white hover:bg-indigo-700"
                >
                  Try again
                </button>
              ) : null}
            </div>
          )}
        </div>

        <aside className="min-w-0 self-start" aria-label="Pipeline status">
          <StatusPane
            data={data}
            now={jobs.now}
            canControl={canControl}
            busyId={jobs.busyId}
            onAction={jobs.requestJobAction}
          />
        </aside>
      </div>
    </div>
  );
}
